import React, { useCallback, useEffect, useState, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  getTemplatePolicies,
  setCurrentPage,
  setPageSize,
  setCurrentParams,
  downloadTemplatePolicy,
  deleteTemplatePolicy,
  getAncestors,
  getEditorConfig
} from "redux/templatePoliciesSlice"
import { ActionDropdown, LoadingSpinner, Button, TwIconButton, Pagination } from 'components/common';
import {
  FaHome,
  FaSearch,
  FaPlus,
  FaUpload,
  FaFolder,
  FaTh,
  FaList,
  FaFilter,
  FaFile,
  FaFolderOpen,
  FaSortAmountDown,
  FaSortAmountUp,
  FaTrash,
  FaDownload,
  FaHdd
} from "react-icons/fa"
import { useParams, useNavigate, Link } from "react-router-dom"
import { debounce } from "helpers/debounce"
import TemplatePolicyItem from "components/app/crm/template/TemplatePolicyItem"
import TemplatePolicyCard from "components/app/crm/template/TemplatePolicyCard"
import TemplatePolicyFolderForm from "components/app/crm/template/TemplatePolicyFolderForm"
import TemplatePolicyUpload from "components/app/crm/template/TemplatePolicyUpload"
import TemplatePolicyViewer from "components/app/crm/template/TemplatePolicyViewer"
import TemplatePolicyDeleteConfirm from "components/app/crm/template/TemplatePolicyDeleteConfirm"
import TemplatePolicyAssignOwner from "components/app/crm/template/TemplatePolicyAssignOwner"
import { toast } from "react-toastify"
import useBreadcrumb from "hooks/useBreadcrumb"

const TemplatePage = () => {
  const {
    data,
    totalRows,
    paginate,
    currentParams,
    currentFolder,
    ancestorData,
    loading,
    downloading
  } = useSelector((state) => state.template_policies)

  const { pageSize, totalPagesCount, fromRecord, toRecord } = paginate
  const profileUser = useSelector((state) => state.profile_user.data)
  
  // Extract permissions from object
  const permissions = profileUser?.permissions || {}
  const canCreate = permissions.v1001 === true
  const canUpload = permissions.v1002 === true

  // View and filter states
  const [viewMode, setViewMode] = useState("grid") // grid or list
  const [sortBy, setSortBy] = useState("name")
  const [sortOrder, setSortOrder] = useState("asc")
  const [filterType, setFilterType] = useState("all") // all, folders, files

  // Selection states
  const [selectedItems, setSelectedItems] = useState([]);

  // Modal states
  const [asset, setAsset] = useState({})
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [showRenameFolder, setShowRenameFolder] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [showViewer, setShowViewer] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [showBulkDelete, setShowBulkDelete] = useState(false)
  const [showAssignOwner, setShowAssignOwner] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams()

  const assetId = params?.id || null
  const templateUrl = "/crm/templates"

  // Breadcrumb: ở trang detail (có id) hiển thị nhãn thay vì id thô
  useBreadcrumb(
    assetId
      ? [
          { label: 'Templates', to: '/crm/templates/list' },
          { label: `Template ${assetId}` }
        ]
      : null
  )

  // Calculate statistics
  const stats = useMemo(() => {
    const folders = data.filter(item => item.asset_type === "folder").length
    const files = data.filter(item => item.asset_type === "file").length
    const totalSize = data
      .filter(item => item.asset_type === "file")
      .reduce((sum, item) => sum + (item.file_size || 0), 0)

    return { folders, files, totalSize }
  }, [data])

  // Filter data (sorting is done server-side)
  const filteredData = useMemo(() => {
    let filtered = [...data]

    // Apply type filter (client-side for UI convenience)
    if (filterType === "folders") {
      filtered = filtered.filter(item => item.asset_type === "folder")
    } else if (filterType === "files") {
      filtered = filtered.filter(item => item.asset_type === "file")
    }

    return filtered
  }, [data, filterType])

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i]
  }

  // Initialize and update params
  useEffect(() => {
    const newCurrentParams = {
      page: currentParams.page || 1,
      per_page: currentParams.per_page || 100,
      sort: {
        attribute: sortBy,
        direction: sortOrder
      }
    }

    if (assetId) {
      newCurrentParams.asset_id = assetId
      dispatch(getAncestors(assetId))
    } else {
      delete newCurrentParams.asset_id
    }

    dispatch(setCurrentParams(newCurrentParams))
    // page/per_page are read as the starting point only; re-running on them would reset the other params on every page change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, sortBy, sortOrder, dispatch])

  // Fetch data when params change
  useEffect(() => {
    if (currentParams && currentParams.page) {
      dispatch(getTemplatePolicies(currentParams))
    }
  }, [currentParams, dispatch])

  const handlePageChange = (page, pageSize) => {
    dispatch(setCurrentPage(page))
    dispatch(setPageSize(pageSize))

    const newCurrentParams = {
      ...currentParams,
      page: page,
      per_page: pageSize,
      sort: {
        attribute: sortBy,
        direction: sortOrder
      }
    }
    dispatch(setCurrentParams(newCurrentParams))
  }

  const handleChangeDebounced = useMemo(
    () => debounce((value) => {
      const newParams = {
        ...currentParams,
        name_cont: value,
        page: 1, // Reset to first page on search
        sort: {
          attribute: sortBy,
          direction: sortOrder
        }
      }
      dispatch(setCurrentParams(newParams))
    }, 500),
    [currentParams, sortBy, sortOrder, dispatch]
  )

  const handleSearchChange = (e) => {
    const { value } = e.target
    handleChangeDebounced(value)
  }

  const handleAction = useCallback((item, actionType) => {
    const itemAssetId = item?.id

    switch (actionType) {
      case "open-folder":
        navigate(`${templateUrl}/${itemAssetId}`)
        break
      case "preview":
        setShowViewer(true)
        break
      case "view-online":
        dispatch(getEditorConfig({ assetId: itemAssetId, forceView: true })).unwrap()
          .then((config) => {
            navigate(`/crm/templates/${itemAssetId}/editor`, {
              state: { config, asset: item, configEndpoint: `/template_policies/${itemAssetId}/editor_config` }
            })
          })
          .catch(() => toast.error("Failed to load editor configuration"))
        break
      case "edit-online":
        dispatch(getEditorConfig({ assetId: itemAssetId, forceView: false })).unwrap()
          .then((config) => {
            navigate(`/crm/templates/${itemAssetId}/editor`, {
              state: { config, asset: item, configEndpoint: `/template_policies/${itemAssetId}/editor_config` }
            })
          })
          .catch(() => toast.error("Failed to load editor configuration"))
        break
      case "assign-owner":
        setShowAssignOwner(true)
        break
      case "download":
        // eslint-disable-next-line no-case-declarations
        const fileName = item?.name
        dispatch(downloadTemplatePolicy({
          assetId: itemAssetId,
          fileName: item.asset_type === "folder" ? `${fileName}.zip` : fileName
        }))
        break
      case "rename":
        setShowRenameFolder(true)
        break
      case "delete":
        setShowDelete(true)
        break
      default:
        break
    }

    setAsset(item)
  }, [navigate, dispatch, templateUrl])

  const handleCreateFolder = () => {
    setAsset({})
    setShowCreateFolder(true)
  }

  const handleUpload = () => {
    if (!assetId) {
      toast.warning("Please navigate into a folder first before uploading files")
      return
    }
    setShowUpload(true)
  }

  const handleFolderSuccess = () => {
    setShowCreateFolder(false)
    setShowRenameFolder(false)
    dispatch(getTemplatePolicies(currentParams))
  }

  const handleUploadSuccess = () => {
    setShowUpload(false)
    dispatch(getTemplatePolicies(currentParams))
  }

  const handleDeleteSuccess = () => {
    setShowDelete(false)
    setSelectedItems([])
    dispatch(getTemplatePolicies(currentParams))
  }

  // Selection handlers
  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId)
      }
      return [...prev, itemId]
    })
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(filteredData.map(item => item.id))
    } else {
      setSelectedItems([])
    }
  }

  const handleBulkDownload = () => {
    selectedItems.forEach(itemId => {
      const item = data.find(d => d.id === itemId)
      if (item) {
        const fileName = item.name
        dispatch(downloadTemplatePolicy({
          assetId: item.id,
          fileName: item.asset_type === "folder" ? `${fileName}.zip` : fileName
        }))
      }
    })
    toast.success(`Downloading ${selectedItems.length} item(s)...`)
  }

  const handleBulkDelete = () => {
    setShowBulkDelete(true)
  }

  const confirmBulkDelete = async () => {
    try {
      for (const itemId of selectedItems) {
        await dispatch(deleteTemplatePolicy(itemId)).unwrap()
      }
      toast.success(`Deleted ${selectedItems.length} item(s) successfully`)
      setShowBulkDelete(false)
      setSelectedItems([])
      dispatch(getTemplatePolicies(currentParams))
    } catch (error) {
      toast.error("Failed to delete some items")
    }
  }

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === "asc" ? "desc" : "asc")
  }

  return (
    <>
      <div className="tw-p-4">
        {/* Header Card */}
        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-solid tw-border-slate-200 tw-mb-4">
          <div className="tw-p-4 sm:tw-p-5">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
              <div className="tw-flex-1 tw-min-w-[260px]">
                <div className="tw-flex tw-items-center">
                  <div className="tw-w-14 tw-h-14 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-gradient-to-br tw-from-primary-500 tw-to-purple-600 tw-shadow-md tw-mr-3">
                    <FaFolderOpen size={28} className="tw-text-white" />
                  </div>
                  <div>
                    <h4 className="tw-text-xl tw-font-semibold tw-text-slate-800 tw-mb-1">Template</h4>
                    <p className="tw-text-sm tw-text-slate-500 tw-mb-0">
                      Manage and organize your company's template and documents
                    </p>
                  </div>
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                {canCreate && (
                  <Button variant="primary" size="md" leftIcon={FaPlus} onClick={handleCreateFolder}>
                    New Folder
                  </Button>
                )}
                {canUpload && (
                  <Button variant="success" size="md" leftIcon={FaUpload} onClick={handleUpload} disabled={!assetId}>
                    Upload
                  </Button>
                )}
              </div>
            </div>

            {/* Statistics */}
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-mt-4">
              {[
                { label: "Folders", value: stats.folders, Icon: FaFolder, accent: "tw-bg-blue-500", iconBg: "tw-bg-blue-50", iconColor: "tw-text-blue-600" },
                { label: "Files", value: stats.files, Icon: FaFile, accent: "tw-bg-emerald-500", iconBg: "tw-bg-emerald-50", iconColor: "tw-text-emerald-600" },
                { label: "Total Items", value: totalRows, Icon: FaFolderOpen, accent: "tw-bg-cyan-500", iconBg: "tw-bg-cyan-50", iconColor: "tw-text-cyan-600" },
                { label: "Total Size", value: formatBytes(stats.totalSize), Icon: FaHdd, accent: "tw-bg-amber-500", iconBg: "tw-bg-amber-50", iconColor: "tw-text-amber-600" },
              ].map(({ label, value, Icon, accent, iconBg, iconColor }) => (
                <div key={label} className="tw-relative tw-overflow-hidden tw-flex tw-items-center tw-gap-4 tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-p-4 tw-shadow-sm">
                  <span className={`tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-1 ${accent}`} />
                  <div className={`tw-w-12 tw-h-12 tw-flex tw-items-center tw-justify-center tw-rounded ${iconBg}`}>
                    <Icon className={iconColor} size={20} />
                  </div>
                  <div>
                    <div className="tw-text-2xl tw-font-bold tw-text-slate-800 tw-leading-none">{value}</div>
                    <div className="tw-text-sm tw-text-slate-500 tw-font-medium tw-mt-1">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-solid tw-border-slate-200">
          <div className="tw-p-4 sm:tw-p-5">
            {/* Breadcrumb Navigation */}
            <nav aria-label="breadcrumb" className="tw-mb-4">
              <ol className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-list-none tw-p-0 tw-m-0">
                <li className="tw-flex tw-items-center tw-font-medium">
                  <Link to={`${templateUrl}/list`} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-blue-600 hover:tw-text-blue-700 tw-no-underline">
                    <FaHome />
                    Home
                  </Link>
                </li>
                {ancestorData?.map((item) => (
                  <li className="tw-flex tw-items-center tw-gap-2 tw-font-medium" key={item.id}>
                    <span className="tw-text-slate-300">/</span>
                    <Link to={`${templateUrl}/${item.id}`} className="tw-text-blue-600 hover:tw-text-blue-700 tw-no-underline">{item.name}</Link>
                  </li>
                ))}
                {currentFolder && (
                  <li className="tw-flex tw-items-center tw-gap-2 tw-font-medium tw-text-slate-500" aria-current="page">
                    <span className="tw-text-slate-300">/</span>
                    {currentFolder}
                  </li>
                )}
              </ol>
            </nav>

            {/* Bulk Actions Toolbar */}
            {selectedItems.length > 0 && (
              <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-blue-50 tw-border tw-border-solid tw-border-blue-200 tw-rounded">
                <div className="tw-flex tw-items-center tw-gap-3">
                  <strong className="tw-text-sm tw-text-blue-700">
                    {selectedItems.length} item(s) selected
                  </strong>
                  <button
                    type="button"
                    className="tw-bg-transparent tw-border-0 tw-text-blue-600 tw-text-xs tw-cursor-pointer tw-no-underline hover:tw-underline tw-p-0"
                    onClick={() => setSelectedItems([])}
                  >
                    Clear Selection
                  </button>
                </div>
                <div className="tw-flex tw-gap-2">
                  <Button variant="secondary" size="sm" leftIcon={FaDownload} onClick={handleBulkDownload}>
                    Download ({selectedItems.length})
                  </Button>
                  {selectedItems.permissions?.allow_delete && (
                    <Button variant="danger" size="sm" leftIcon={FaTrash} onClick={handleBulkDelete}>
                      Delete ({selectedItems.length})
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Toolbar */}
            <div className="tw-mb-4 tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-gap-3">
              {/* Search */}
              <div className="tw-flex tw-items-center tw-gap-2 tw-h-9 tw-px-3 tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200 tw-rounded md:tw-w-80 focus-within:tw-border-blue-400 focus-within:tw-ring-2 focus-within:tw-ring-blue-500/20 tw-transition-colors">
                <FaSearch className="tw-text-slate-400 tw-shrink-0" size={12} />
                <input
                  type="text"
                  placeholder="Search templates and files..."
                  onChange={handleSearchChange}
                  className="tw-w-full tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-slate-800 placeholder:tw-text-slate-400"
                />
              </div>

              {/* Right controls */}
              <div className="tw-flex tw-justify-end tw-items-center tw-gap-2 tw-flex-wrap md:tw-ml-auto">
                {/* Filter Dropdown */}
                <ActionDropdown
                  unstyledTrigger
                  triggerClassName="tw-inline-flex tw-items-center tw-gap-2 tw-h-9 tw-px-3 tw-rounded tw-bg-white tw-border tw-border-solid tw-border-slate-200 tw-text-slate-700 hover:tw-bg-slate-50 tw-text-xs tw-font-semibold tw-cursor-pointer tw-transition-colors"
                  trigger={(
                    <>
                      <FaFilter />
                      {filterType === "all" ? "All Items" : filterType === "folders" ? "Folders" : "Files"}
                    </>
                  )}
                  items={[
                    { label: 'All Items', onClick: () => setFilterType("all") },
                    { label: 'Folders Only', onClick: () => setFilterType("folders") },
                    { label: 'Files Only', onClick: () => setFilterType("files") }
                  ]}
                />

                {/* Sort Dropdown */}
                <ActionDropdown
                  unstyledTrigger
                  triggerClassName="tw-inline-flex tw-items-center tw-gap-2 tw-h-9 tw-px-3 tw-rounded tw-bg-white tw-border tw-border-solid tw-border-slate-200 tw-text-slate-700 hover:tw-bg-slate-50 tw-text-xs tw-font-semibold tw-cursor-pointer tw-transition-colors"
                  trigger={(
                    <>
                      <FaSortAmountDown />
                      Sort: {sortBy === "name" ? "Name" : sortBy === "date" ? "Date" : "Size"}
                    </>
                  )}
                  items={[
                    { label: 'Name', onClick: () => setSortBy("name") },
                    { label: 'Date Created', onClick: () => setSortBy("date") },
                    { label: 'File Size', onClick: () => setSortBy("size") }
                  ]}
                />

                {/* Sort Order Button */}
                <TwIconButton
                  icon={sortOrder === "asc" ? FaSortAmountUp : FaSortAmountDown}
                  size="lg"
                  variant="secondary"
                  onClick={toggleSortOrder}
                  title={sortOrder === "asc" ? "Ascending" : "Descending"}
                />

                {/* View Mode Toggle */}
                <div className="tw-flex tw-items-center tw-gap-1 tw-p-1 tw-bg-slate-100 tw-border tw-border-solid tw-border-slate-200 tw-rounded">
                  <TwIconButton icon={FaTh} size="sm" variant="ghost" active={viewMode === "grid"} onClick={() => setViewMode("grid")} title="Grid view" />
                  <TwIconButton icon={FaList} size="sm" variant="ghost" active={viewMode === "list"} onClick={() => setViewMode("list")} title="List view" />
                </div>
              </div>
            </div>

            {/* Content Area */}
            {loading ? (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-py-16 tw-min-h-[400px]">
                <LoadingSpinner size="lg" />
                <p className="tw-mt-3 tw-text-slate-500">Loading your templates...</p>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-py-16 tw-min-h-[400px]">
                <div className="tw-w-20 tw-h-20 tw-mb-5 tw-flex tw-items-center tw-justify-center tw-rounded-2xl tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200 tw-text-slate-300">
                  <FaFolder size={36} />
                </div>
                <h5 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-2">
                  {data.length === 0 ? "No templates yet" : "No items found"}
                </h5>
                <p className="tw-text-sm tw-text-slate-500 tw-mb-5 tw-max-w-md">
                  {data.length === 0
                    ? assetId
                      ? "This folder is empty. Upload some files to get started"
                      : "Get started by creating your first folder"
                    : "Try adjusting your filters or search criteria"}
                </p>
                {assetId && canUpload && data.length === 0 && (
                  <Button variant="success" size="md" leftIcon={FaUpload} onClick={handleUpload}>
                    Upload Files
                  </Button>
                )}
                {!assetId && canCreate && data.length === 0 && (
                  <Button variant="primary" size="md" leftIcon={FaPlus} onClick={handleCreateFolder}>
                    Create First Folder
                  </Button>
                )}
              </div>
            ) : viewMode === "grid" ? (
              <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-5 xl:tw-grid-cols-6 tw-gap-3">
                {filteredData.map((item) => (
                  <TemplatePolicyCard
                    key={item.id}
                    item={item}
                    onAction={handleAction}
                    canRename={item.permissions.allow_rename}
                    canDelete={item.permissions.allow_delete}
                    isSelected={selectedItems.includes(item.id)}
                    onSelect={handleSelectItem}
                  />
                ))}
              </div>
            ) : (
              <div className="tw-overflow-x-auto tw-border tw-border-solid tw-border-slate-200 tw-rounded-lg">
                <table className="tw-w-full tw-text-sm tw-border-collapse">
                  <thead>
                    <tr className="tw-bg-slate-50 tw-border-b-2 tw-border-solid tw-border-slate-200">
                      <th className="tw-p-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600" style={{ width: "50px" }}>
                        <input
                          type="checkbox"
                          className="tw-w-4 tw-h-4 tw-cursor-pointer"
                          checked={selectedItems.length === filteredData.length && filteredData.length > 0}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th className="tw-p-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">Name</th>
                      <th className="tw-p-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600" style={{ width: "120px" }}>Type</th>
                      <th className="tw-p-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600" style={{ width: "150px" }}>Created By</th>
                      <th className="tw-p-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600" style={{ width: "150px" }}>Date</th>
                      <th className="tw-p-3 tw-text-center tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600" style={{ width: "80px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item) => (
                      <TemplatePolicyItem
                        key={item.id}
                        item={item}
                        onAction={handleAction}
                        canDelete={item.permissions.allow_delete}
                        canRename={item.permissions.allow_rename}
                        isSelected={selectedItems.includes(item.id)}
                        onSelect={handleSelectItem}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalRows > 0 && (
              <div className="tw-mt-4">
                <Pagination
                  totalRows={totalRows}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                  currentPage={currentParams.page}
                  totalPagesCount={totalPagesCount}
                  fromRecord={fromRecord}
                  toRecord={toRecord}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateFolder && (
        <TemplatePolicyFolderForm
          show={showCreateFolder}
          onHide={() => setShowCreateFolder(false)}
          onSuccess={handleFolderSuccess}
          parentId={assetId}
          mode="create"
        />
      )}

      {showRenameFolder && (
        <TemplatePolicyFolderForm
          show={showRenameFolder}
          onHide={() => setShowRenameFolder(false)}
          onSuccess={handleFolderSuccess}
          asset={asset}
          mode="rename"
        />
      )}

      {showUpload && (
        <TemplatePolicyUpload
          show={showUpload}
          onHide={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
          parentId={assetId}
        />
      )}

      {showViewer && (
        <TemplatePolicyViewer
          show={showViewer}
          onHide={() => setShowViewer(false)}
          asset={asset}
        />
      )}

      {showDelete && (
        <TemplatePolicyDeleteConfirm
          show={showDelete}
          onHide={() => setShowDelete(false)}
          onSuccess={handleDeleteSuccess}
          asset={asset}
        />
      )}

      {showBulkDelete && (
        <TemplatePolicyDeleteConfirm
          show={showBulkDelete}
          onHide={() => setShowBulkDelete(false)}
          onSuccess={confirmBulkDelete}
          asset={{ name: `${selectedItems.length} items`, asset_type: "folder" }}
          isBulk={true}
        />
      )}

      {showAssignOwner && (
        <TemplatePolicyAssignOwner
          show={showAssignOwner}
          onHide={() => setShowAssignOwner(false)}
          asset={asset}
          onSuccess={() => {
            setShowAssignOwner(false)
            dispatch(getTemplatePolicies(currentParams))
          }}
        />
      )}

      {downloading && (
        <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-black/75 tw-backdrop-blur-[2px]">
          <div className="tw-text-center">
            <LoadingSpinner size="lg" />
            <p className="tw-mt-3 tw-text-white tw-text-lg tw-font-medium">Preparing your download...</p>
          </div>
        </div>
      )}
    </>
  )
}

export default TemplatePage
